import Component from '@glimmer/component';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { tracked } from '@glimmer/tracking';

import type RoomModel from 'admin-panel/models/room';
import type { VisibilityMode } from './page-form';

interface PanelCategoryRoomPickerArgs {
  contextKey?: string;
  rooms: RoomModel[];
  visibilityMode: VisibilityMode;
  selectedRoomIds: number[];
  roomsLoading?: boolean;
  roomsLoadFailed?: boolean;
  onModeChange?: (mode: VisibilityMode) => void;
  onRoomIdsChange?: (roomIds: number[]) => void;
  onRetryRooms?: () => void;
}

export default class PanelCategoryRoomPickerComponent extends Component<PanelCategoryRoomPickerArgs> {
  @tracked
  isExpanded = false;

  get selectedCount() {
    return this.args.selectedRoomIds.length;
  }

  get isSpecificExpanded() {
    return this.args.visibilityMode === 'specific' && this.isExpanded;
  }

  get canShowRooms() {
    return !this.args.roomsLoading && !this.args.roomsLoadFailed;
  }

  get specificButtonId() {
    return `${guidFor(this)}-specific-button`;
  }

  get specificPanelId() {
    return `${guidFor(this)}-specific-panel`;
  }

  @action
  selectMode(mode: VisibilityMode) {
    if (mode === 'specific' && this.args.visibilityMode === 'specific') {
      this.isExpanded = !this.isExpanded;
      return;
    }

    this.isExpanded = mode === 'specific';
    this.args.onModeChange?.(mode);
  }

  @action
  syncExternalMode() {
    if (this.args.visibilityMode !== 'specific') {
      this.isExpanded = false;
    }
  }

  @action
  collapseForContext() {
    this.isExpanded = false;
  }

  @action
  retryRooms() {
    this.args.onRetryRooms?.();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Panel::Category::RoomPicker': typeof PanelCategoryRoomPickerComponent;
    'panel/category/room-picker': typeof PanelCategoryRoomPickerComponent;
  }
}
